import React from 'react';
import { Box, Card, CardHeader, CardContent, Collapse, Grid, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableFooter, TablePagination, TableRow, Typography, } from '@mui/material';
import { KeyboardArrowDown as KeyboardArrowDownIcon, KeyboardArrowUp as KeyboardArrowUpIcon } from '@mui/icons-material';
import { defineMessages, useIntl } from 'react-intl';
import { useQuery } from 'react-query';
import { GoogleMapProvider, PathMap } from '../map/google';
import API from '@wheeebo/polaris-api';
import { CircleLoader, LoadError } from '../loader';
import { VehiclePanel } from '../panel';
const INITIAL_ITEM_COUNT = 5;
const msgs = defineMessages({
    headerTitle: {
        id: 'pages.member.member.FlightLogListCard.header.title',
        defaultMessage: 'Flight Logs',
    },
    headerSubTitle: {
        id: 'pages.member.member.FlightLogListCard.header.subTitle',
        defaultMessage: 'List of past flight logs operated by this member',
    },
});
function DateRow({ label, startAt, endAt }) {
    const intl = useIntl();
    return (React.createElement(React.Fragment, null,
        React.createElement(Grid, { item: true, xs: 4, sx: { display: 'flex', alignItems: 'center' } }, label),
        React.createElement(Grid, { item: true, xs: 8 },
            React.createElement(Grid, { container: true },
                React.createElement(Grid, { item: true, xs: 3 }, "From:"),
                React.createElement(Grid, { item: true, xs: 9 }, startAt ? intl.formatTime(startAt, { year: 'numeric', month: 'numeric', day: '2-digit' }) : null),
                React.createElement(Grid, { item: true, xs: 3 }, "To:"),
                React.createElement(Grid, { item: true, xs: 9 }, endAt ? intl.formatTime(endAt, { year: 'numeric', month: 'numeric', day: '2-digit' }) : null)))));
}
function Row({ apiKey, flightLog, onMissionSelected, onVehicleSelected, onFlightPathSelected }) {
    const intl = useIntl();
    const [open, setOpen] = React.useState(false);
    return (React.createElement(React.Fragment, null,
        React.createElement(TableRow, { hover: true, onClick: () => setOpen((e) => !e) },
            React.createElement(TableCell, { sx: { width: '100%' } },
                React.createElement(Stack, { gap: 1 },
                    React.createElement(Stack, { direction: "row", gap: 1, sx: { display: 'flex', alignItems: 'center' } },
                        React.createElement(Box, null,
                            React.createElement(Typography, { variant: "body2", sx: { display: 'inline' } }, flightLog.flightPlan.flightPath.name),
                            React.createElement(Typography, { variant: "body2", sx: { display: 'inline', opacity: 0.7 } }, ` at `),
                            React.createElement(Typography, { variant: "body2", sx: { display: 'inline', textDecoration: 'underline' } }, intl.formatTime(flightLog.startAt, { year: 'numeric', month: 'numeric', day: '2-digit' }))))),
                flightLog.path ? (React.createElement(Box, { sx: { width: '100%', minHeight: 180, position: 'relative', flex: 1 } },
                    React.createElement(GoogleMapProvider, null,
                        React.createElement(PathMap, { apiKey: apiKey, path: flightLog.path })))) : null),
            React.createElement(TableCell, { align: "right" }, open ? React.createElement(KeyboardArrowUpIcon, { fontSize: "small" }) : React.createElement(KeyboardArrowDownIcon, { fontSize: "small" }))),
        React.createElement(TableRow, null,
            React.createElement(TableCell, { colSpan: 2, style: { paddingBottom: open ? undefined : 0, paddingTop: open ? undefined : 0 } },
                React.createElement(Collapse, { in: open },
                    React.createElement(Grid, { container: true, spacing: 1 },
                        React.createElement(Grid, { item: true, xs: 4, sx: { display: 'flex', alignItems: 'center' } }, "Mission"),
                        React.createElement(Grid, { item: true, xs: 8, sx: { cursor: 'pointer' }, onClick: () => flightLog?.flightPlan?.flightPath?.mission
                                ? onMissionSelected(flightLog.flightPlan.flightPath.mission.id)
                                : undefined }, flightLog.flightPlan.flightPath.mission.name),
                        React.createElement(Grid, { item: true, xs: 4, sx: { display: 'flex', alignItems: 'center' } }, "Flight Path"),
                        React.createElement(Grid, { item: true, xs: 8, sx: { cursor: 'pointer' }, onClick: () => flightLog?.flightPlan?.flightPath?.mission
                                ? onFlightPathSelected(flightLog.flightPlan.flightPath.mission.id, flightLog.flightPlan.flightPath.id)
                                : undefined }, flightLog.flightPlan.flightPath.name),
                        React.createElement(Grid, { item: true, xs: 4 }, "Vehicle"),
                        React.createElement(Grid, { item: true, xs: 8 },
                            React.createElement(VehiclePanel, { vehicle: flightLog.vehicle, onClick: () => onVehicleSelected(flightLog.vehicle.id) })),
                        React.createElement(DateRow, { label: "Date", startAt: flightLog.startAt, endAt: flightLog.endAt })))))));
}
function FlightLogListCard({ apiKey, memberId, onMissionSelected, onVehicleSelected, onFlightPathSelected, ...props }) {
    const intl = useIntl();
    const [page, setPage] = React.useState(0);
    const options = {
        limit: INITIAL_ITEM_COUNT,
        offset: page * INITIAL_ITEM_COUNT,
        operatorId: memberId,
        preload: ['vehicle', 'flight_plan', 'flight_path', 'mission', 'path'],
    };
    const { data, isError, isLoading } = useQuery(API.FlightLogs.listKey(options), () => API.FlightLogs.list(options));
    return (React.createElement(Card, { ...props },
        React.createElement(CardHeader, { title: intl.formatMessage(msgs.headerTitle), subheader: intl.formatMessage(msgs.headerSubTitle) }),
        React.createElement(CardContent, null,
            isLoading ? React.createElement(CircleLoader, null) : null,
            isError ? React.createElement(LoadError, null) : null,
            data && data.total > 0 ? (React.createElement(TableContainer, { component: Paper, elevation: 0 },
                React.createElement(Table, null,
                    React.createElement(TableBody, null, data?.items?.map((e) => (React.createElement(Row, { key: e.id, apiKey: apiKey, flightLog: e, onMissionSelected: onMissionSelected, onVehicleSelected: onVehicleSelected, onFlightPathSelected: onFlightPathSelected })))),
                    React.createElement(TableFooter, null,
                        React.createElement(TableRow, null,
                            React.createElement(TablePagination, { rowsPerPageOptions: [INITIAL_ITEM_COUNT], count: data?.total || 0, rowsPerPage: INITIAL_ITEM_COUNT, page: page, SelectProps: {
                                    inputProps: { 'aria-label': 'rows per page' },
                                }, onPageChange: (_, newPage) => setPage(newPage) })))))) : (React.createElement(Typography, { variant: "body2" }, "No record found.")))));
}
export default React.memo(FlightLogListCard);
